import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 快应用输入参数
 */
export interface AppGenerationInput {
  /**
   * 字段名
   */
  field: string;
  /**
   * 字段值（可以是任意类型）
   */
  value: any;
  /**
   * 节点ID（当field重复时指定，或者严格遵循查询顺序）
   */
  node_id?: string;
}

/**
 * 快应用生成参数
 */
export interface AppGenerationParams {
  /**
   * 快应用模板ID
   */
  template_id: string;
  /**
   * 输入参数列表
   */
  inputs: AppGenerationInput[];
}

/**
 * 快应用生成资源
 */
export interface AppGenerationResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * 快应用生成完整结果
 */
export interface AppGenerationResult {
  /**
   * 任务ID
   */
  id: string;
  /**
   * 任务状态
   */
  status: string;
  /**
   * 模型名称
   */
  model: string;
  /**
   * 输出内容（如果有）
   */
  output?: any[];
  /**
   * 使用情况统计
   */
  usage?: {
    cost?: string;
    used?: number;
    [key: string]: any;
  };
  /**
   * 其他字段
   */
  [key: string]: any;
}

/**
 * appGeneration
 *
 * 快应用生成任务接口
 *
 * 根据模板ID和输入参数创建快应用生成任务，并等待任务完成。
 * 与标准任务生成接口功能相同，但入参格式不同。
 *
 * Features:
 * - 基于模板快速生成内容
 * - 支持自定义输入参数
 * - 自动轮询等待任务完成
 *
 * API Documentation: https://yapi.internal.ops.haiyiai.tech/project/115/interface/api/20216
 *
 * @example
 * ```typescript
 * import { initSeacloud, appGeneration } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await appGeneration({
 *   template_id: 'd26trpte878eqsnm3bjg',
 *   inputs: [
 *     { field: 'prompt1', value: '你好' }
 *   ]
 * });
 *
 * console.log(result); // 返回完整的任务结果
 * ```
 *
 * @param params 快应用生成参数
 * @returns 完整的任务结果
 */
export async function appGeneration(
  params: AppGenerationParams,
): Promise<AppGenerationResult> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'comfyui',
      input: [{ params }],
    },
    pollingOptions,
  );

  return result as AppGenerationResult;
}
